import { topicDislikeButtonMarkup, topicFavoriteButtonMarkup, topicLikeButtonMarkup } from './topicReactionMarkup';

type PosterMetricKind = 'chapters' | 'duration';
type PosterMetricVariant = 'poster' | 'topic-hero' | 'topic-card';
type PosterCategoryVariant = 'poster' | 'topic-hero' | 'topic-card';

export type TopicPosterContentInput = {
	title: string;
	subtitle?: string;
	/** Legacy single-category input. Prefer categoryLabels where classification is available. */
	category?: string;
	/** Ordered Topic classification labels (primary followed by secondary labels). */
	categoryLabels?: readonly string[];
	chapterCount?: number;
	audioMinutes?: number;
	coverBadge?: 'github' | 'new' | 'trending';
	topicId?: string;
	voteValue?: number;
	isFavorite?: boolean;
	titleTag?: 'h1' | 'h2' | 'h3' | 'h4';
	ids?: Partial<Record<'title' | 'subtitle' | 'category' | 'primaryCategory' | 'metrics' | 'chapters' | 'duration' | 'like' | 'dislike' | 'favorite', string>>;
	bindings?: Partial<Record<'title' | 'subtitle' | 'category', string>>;
	includeReactions?: boolean;
	metricVariant?: PosterMetricVariant;
	/** Existing semantic metric region retained by Chapter's desktop contract. */
	metricsMarkup?: string;
	categoryVariant?: PosterCategoryVariant;
	titleClass?: string;
	subtitleClass?: string;
	/** Pre-escaped extra attributes for the title tag (e.g. data-driven affixes). */
	titleAttributes?: string;
	loading?: boolean;
};

export type TopicPosterContentSlots = {
	badge: string;
	title: string;
	subtitle: string;
	metrics: string;
	categories: string;
	reactions: string;
};

/**
 * The complete poster anatomy used by every cover-style Topic surface.
 *
 * Consumers may retain their semantic heading and interaction ids in the
 * supplied copy/footer markup, but they must not rearrange the image, copy,
 * or footer into a different tree. This makes the mobile visual grammar a
 * real shared component rather than a collection of matching CSS selectors.
 */
export type TopicPosterSurfaceInput = {
	poster: TopicPosterContentInput;
	coverClass?: string;
	coverId?: string;
	/** Artwork belongs only to the common cover layer. */
	coverImageUrl?: string;
	/** Badge-only slot; the common cover owns artwork and its one scrim. */
	badgeMarkup?: string;
	contentClass?: string;
	/** Legacy layout alias; semantic copy children remain renderer-owned. */
	copyClass?: string;
	footerClass?: string;
	/** Chapter context is the only copy prefix allowed by the common anatomy. */
	contextMarkup?: string;
	/** Chapter transport is the only footer variant that replaces categories/reactions. */
	footerMode?: 'topic' | 'chapter-transport';
	footerMarkup?: string;
	/** Card-only menu markup remains in the shared footer position. */
	categoryMarkup?: string;
	reactionsMarkup?: string;
};

export function topicPosterSurfaceMarkup(input: TopicPosterSurfaceInput): string {
	const slots = topicPosterContentSlots(input.poster);
	const coverClass = input.coverClass ? ` ${escapeAttribute(input.coverClass)}` : '';
	const contentClass = input.contentClass ? ` ${escapeAttribute(input.contentClass)}` : '';
	const copyClass = input.copyClass ? ` ${escapeAttribute(input.copyClass)}` : '';
	const footerClass = input.footerClass ? ` ${escapeAttribute(input.footerClass)}` : '';
	const coverId = input.coverId ? ` id="${escapeAttribute(input.coverId)}"` : '';
	const coverStyle = input.coverImageUrl ? ` style="background-image:url('${escapeAttribute(input.coverImageUrl)}')"` : '';
	const footer = input.footerMode === 'chapter-transport'
		? input.footerMarkup ?? ''
		: `${input.categoryMarkup ?? slots.categories}${input.reactionsMarkup ?? slots.reactions}`;
	return `<div class="topic-poster-cover${coverClass}"${coverId}${coverStyle}>${input.badgeMarkup ?? slots.badge}</div><div class="topic-poster-content${contentClass}"><div class="topic-poster-copy${copyClass}">${input.contextMarkup ?? ''}${slots.title}${slots.subtitle}${slots.metrics}</div><div class="topic-poster-footer${footerClass}">${footer}</div></div>`;
}

const CHAPTER_ICON = '<svg aria-hidden="true" focusable="false" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="metric-icon topic-metric-icon--mobile"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path></svg>';
const DURATION_ICON = '<svg aria-hidden="true" focusable="false" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="metric-icon topic-metric-icon--mobile"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>';

/**
 * One semantic poster renderer for Topic landings, chapter panels and Now
 * Playing. Consumers place the returned slots in their existing layout, so a
 * Topic page can keep its CTA/footer and desktop keyword column without
 * cloning the poster's title, classification, metric, badge, or reaction DOM.
 */
export function topicPosterContentSlots(input: TopicPosterContentInput): TopicPosterContentSlots {
	const tag = input.titleTag ?? 'h2';
	const id = (name: keyof NonNullable<TopicPosterContentInput['ids']>) => {
		const value = input.ids?.[name];
		return value ? ` id="${escapeAttribute(value)}"` : '';
	};
	const bind = (name: keyof NonNullable<TopicPosterContentInput['bindings']>) => {
		const value = input.bindings?.[name];
		return value ? ` data-bind="${escapeAttribute(value)}"` : '';
	};
	const titleClass = classAttribute('topic-poster-title', input.titleClass);
	const subtitleClass = classAttribute('topic-poster-subtitle', input.subtitleClass);
	const labels = normalizeLabels(input.categoryLabels ?? (input.category ? [input.category] : []));
	const chapterCount = finitePositive(input.chapterCount);
	const audioMinutes = finitePositive(input.audioMinutes);
	const metrics = metricSlots(chapterCount, audioMinutes);
	const badge = input.coverBadge
		? `<span class="cover-badge topic-cover-badge browse-cover-badge browse-cover-badge--${input.coverBadge}${input.coverBadge === 'github' ? ' source-badge' : ''}">${badgeLabel(input.coverBadge)}</span>`
		: '';
	const loadingTitle = '<span class="topic-hero-loading-line" aria-hidden="true"></span><span class="topic-hero-loading-line topic-hero-loading-line--short" aria-hidden="true"></span>';
	const title = `<${tag}${id('title')}${titleClass}${bind('title')}${input.titleAttributes ?? ''}${input.loading ? ' data-hero-loading-slot="title"' : ''}>${input.loading ? loadingTitle : escapeHtml(input.title)}</${tag}>`;
	const subtitle = input.loading || input.subtitle || input.metricVariant === 'topic-card' || input.ids?.subtitle
		? `<p${id('subtitle')}${subtitleClass}${bind('subtitle')}${input.loading ? ' data-hero-loading-slot="subtitle"' : ''}>${input.loading ? loadingTitle : escapeHtml(input.subtitle ?? '')}</p>`
		: '';
	return {
		badge,
		title,
		subtitle,
		metrics: input.metricsMarkup ?? renderMetrics(metrics, input, id),
		categories: renderCategories(labels, input, id),
		reactions: input.includeReactions === false
			? ''
			: `<span class="topic-actions topic-poster-actions">${topicLikeButtonMarkup(input.topicId ?? '', input.voteValue ?? 0, input.ids?.like)}${topicDislikeButtonMarkup(input.topicId ?? '', input.voteValue ?? 0, input.ids?.dislike)}${topicFavoriteButtonMarkup(input.topicId ?? '', input.isFavorite ?? false, input.ids?.favorite)}</span>`,
	};
}

/**
 * Compatibility fragment for legacy hosts while they migrate to the complete
 * surface renderer. New cover-style hosts must call topicPosterSurfaceMarkup.
 */
export function topicPosterContentMarkup(input: TopicPosterContentInput): string {
	const slots = topicPosterContentSlots(input);
	return `${slots.badge}<div class="topic-poster-copy">${slots.title}${slots.subtitle}${slots.metrics}</div><div class="topic-poster-footer">${slots.categories}${slots.reactions}</div>`;
}

function metricSlots(chapterCount: number | undefined, audioMinutes: number | undefined): Array<{ kind: PosterMetricKind; label: string; icon: string }> {
	const metrics: Array<{ kind: PosterMetricKind; label: string; icon: string }> = [];
	if (chapterCount) metrics.push({ kind: 'chapters', label: `${chapterCount} chapter${chapterCount === 1 ? '' : 's'}`, icon: CHAPTER_ICON });
	if (audioMinutes) metrics.push({ kind: 'duration', label: `${audioMinutes} min`, icon: DURATION_ICON });
	return metrics;
}

function renderMetrics(metrics: Array<{ kind: PosterMetricKind; label: string; icon: string }>, input: TopicPosterContentInput, id: (name: keyof NonNullable<TopicPosterContentInput['ids']>) => string): string {
	const variant = input.metricVariant ?? 'poster';
	if (input.loading && variant === 'topic-hero') return `<div class="topic-poster-metrics tdb-meta"${id('metrics')} data-hero-loading-slot="metrics"><span class="tdb-metric tdb-metric--loading"${id('chapters')}></span><span class="tdb-metric tdb-metric--loading"${id('duration')}></span></div>`;
	if (variant === 'topic-card') {
		const chapters = metrics.find((metric) => metric.kind === 'chapters') ?? { kind: 'chapters' as const, label: '0 chapters', icon: CHAPTER_ICON };
		const duration = metrics.find((metric) => metric.kind === 'duration') ?? { kind: 'duration' as const, label: '-', icon: DURATION_ICON };
		return `<p class="topic-poster-metrics topic-meta"${id('metrics')}>${chapters.icon}<span${id(chapters.kind)}>${chapters.label}</span> · ${duration.icon}<span${id(duration.kind)}>${duration.label}</span></p>`;
	}
	if (!metrics.length) return '';
	if (variant === 'topic-hero') return `<div class="topic-poster-metrics tdb-meta"${id('metrics')}>${metrics.map((metric) => `<span class="tdb-metric"${id(metric.kind)}>${metric.icon}<span>${metric.label}${metric.kind === 'duration' ? '<span class="topic-metric-total"> total</span>' : ''}</span></span>`).join('')}</div>`;
	return `<span class="topic-poster-metrics"${id('metrics')}>${metrics.map((metric, index) => `<span${id(metric.kind)}>${metric.label}</span>${index < metrics.length - 1 ? ' · ' : ''}`).join('')}</span>`;
}

function renderCategories(labels: string[], input: TopicPosterContentInput, id: (name: keyof NonNullable<TopicPosterContentInput['ids']>) => string): string {
	const variant = input.categoryVariant ?? 'poster';
	if (input.loading && variant === 'topic-hero') return `<div class="topic-poster-categories tdb-pills"${id('category')} data-hero-loading-slot="category" hidden><span class="pill pill-category topic-hero-loading-pill"${id('primaryCategory')} data-bind="topic.category" data-hero-loading-slot="category-pill" hidden></span></div>`;
	if (!labels.length && variant !== 'topic-card') return '';
	if (variant === 'topic-hero') return `<div class="topic-poster-categories tdb-pills"${id('category')}>${labels.map((label, index) => `<span class="pill pill-category${index === 0 ? ' topic-cat-pill--primary' : ''}"${index === 0 && input.ids?.primaryCategory ? ` id="${escapeAttribute(input.ids.primaryCategory)}" data-bind="topic.category"` : ''}>${escapeHtml(label)}</span>`).join('')}</div>`;
	if (variant === 'topic-card') return `<div class="topic-poster-categories topic-category-summary"${id('category')}>${labels.map((label) => `<span class="pill pill-category">${escapeHtml(label)}</span>`).join('')}</div>`;
	return `<span class="topic-poster-categories topic-poster-category"${id('category')}>${labels.map((label) => `<span class="pill pill-category">${escapeHtml(label)}</span>`).join('')}</span>`;
}

function classAttribute(required: string, extra?: string): string {
	return ` class="${escapeAttribute([required, extra].filter(Boolean).join(' '))}"`;
}

function normalizeLabels(labels: readonly string[]): string[] {
	const seen = new Set<string>();
	return labels.flatMap((candidate) => {
		const label = candidate.trim();
		if (!label || seen.has(label)) return [];
		seen.add(label);
		return [label];
	});
}

function finitePositive(value: number | undefined): number | undefined {
	return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : undefined;
}

function badgeLabel(value: NonNullable<TopicPosterContentInput['coverBadge']>): string {
	return value === 'github' ? 'GitHub' : value === 'trending' ? 'Trending' : 'New';
}

function escapeHtml(value: string): string {
	return value.replace(/[&<>"']/g, (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[character] ?? character));
}

function escapeAttribute(value: string): string {
	return escapeHtml(value);
}
